import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Loader2, Coins, Upload, Check, Sparkles, Save, Download, Plus, FolderOpen, X, Library, FolderLock } from "lucide-react";
import { mistApi, streamAsset, thumbAsset, posterAsset, downloadAsset } from "@/api/mist";
import { api } from "@/api/client";
import { uploadMedia } from "@/components/gallery/galleryApi";
import SaveDestinations from "@/components/gallery/SaveDestinations";
import PlansPanel from "@/components/billing/PlansPanel";

/* Live spendable-Coin balance chip with a one-tap top-up. Mounted in the Mist
   Create + Mist Music headers so the operator always sees their balance and can
   refill the instant they run low (before a 402 ever surfaces). Reads the exact
   ledger the Atomic Guard charges (/monetization/entitlement) and auto-refreshes
   on the `mist:coins-updated` event fired after every charge/release. */
export function CoinBalanceChip() {
  const [balance, setBalance] = useState(null);
  const [free, setFree] = useState(false);
  const [showTopUp, setShowTopUp] = useState(false);

  const load = useCallback(() => {
    api.get("/monetization/entitlement?feature=gen_image")
      .then((r) => { setBalance(Number(r.data?.balance ?? 0)); setFree(!!r.data?.free); })
      .catch(() => setBalance(null));
  }, []);

  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("mist:coins-updated", h);
    return () => window.removeEventListener("mist:coins-updated", h);
  }, [load]);

  const low = !free && balance !== null && balance <= 5;

  return (
    <>
      <button
        type="button"
        onClick={() => setShowTopUp(true)}
        data-testid="coin-balance-chip"
        title="Your spendable Coins — tap to top up"
        className={[
          "flex items-center gap-1.5 rounded-full border-2 px-3 py-1.5 font-mono text-[11px] font-black transition-colors active:scale-95",
          low ? "border-red-500/60 bg-red-500/10 text-red-300"
              : "border-[#FFCC00]/50 bg-[#FFCC00]/10 text-[#FFCC00]",
        ].join(" ")}
      >
        <Coins size={13} />
        <span data-testid="coin-balance-value">{free ? "∞" : balance === null ? "—" : balance}</span>
        <span className="grid h-4 w-4 place-items-center rounded-full bg-[#FFCC00] text-black" data-testid="coin-topup-btn">
          <Plus size={11} strokeWidth={3} />
        </span>
      </button>
      {showTopUp && <PlansPanel onBack={() => { setShowTopUp(false); load(); }} />}
    </>
  );
}


/* Coin-burn note shown on every generative hook (5 MiLink). */
export function CoinNote() {
  return (
    <span className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-widest text-white/60">
      <Coins size={11} className="text-gold" /> 5 MiLink / run
    </span>
  );
}

/* Sticky bottom generate button optimized for single-thumb use. */
export function GenerateBar({ onRun, busy, label = "Generate", disabled }) {
  return (
    <div className="shrink-0 border-t-4 border-[#2a2a2a] bg-[#141414] px-4 py-3 flex items-center justify-between gap-3" data-testid="mist-generate-bar">
      <CoinNote />
      <button onClick={onRun} disabled={busy || disabled} data-testid="mist-generate-btn"
        className="flex items-center gap-2 bg-gold text-black font-black font-mono text-xs uppercase tracking-widest px-6 py-3 rounded-full border-2 border-[#2a2a2a] shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-50">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} {busy ? "Running…" : label}
      </button>
    </div>
  );
}

/* Renders the invoke result: text content and/or a streamed vault asset. */
export function ResultCard({ result }) {
  const [src, setSrc] = useState(null);
  const asset = result?.asset;
  const track = asset?.track;
  useEffect(() => {
    let url;
    if (asset?.id && (track === "image" || track === "video" || track === "music")) {
      streamAsset(asset.id).then((u) => { url = u; setSrc(u); }).catch(() => {});
    }
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [asset?.id, track]);

  if (!result) return null;
  return (
    <div className="mt-4 border-4 border-[#2a2a2a] rounded-2xl bg-[#141414] p-3 shadow-hard" data-testid="mist-result">
      <div className="flex items-center gap-2 mb-2">
        <span className="font-mono text-[8px] uppercase tracking-widest bg-charcoal text-gold px-2 py-0.5 rounded-full">{result.engine || "done"}</span>
        {result.charge?.charged ? <span className="font-mono text-[8px] uppercase tracking-widest text-white/50">−{result.charge.amount} MiLink</span>
          : result.charge?.bypass ? <span className="font-mono text-[8px] uppercase tracking-widest text-white/50">protected</span> : null}
      </div>
      {result.content && <p className="font-mono text-[11px] text-white/85 leading-relaxed whitespace-pre-wrap">{result.content}</p>}
      {track === "image" && (src ? <img src={src} alt="generated" className="w-full rounded-xl border-2 border-[#2a2a2a]" data-testid="mist-result-image" /> : <Loader2 size={18} className="text-gold animate-spin" />)}
      {track === "video" && (src ? <video src={src} controls autoPlay muted loop playsInline className="w-full rounded-xl border-2 border-[#2a2a2a]" data-testid="mist-result-video" /> : <Loader2 size={18} className="text-gold animate-spin" />)}
      {track === "music" && (src ? <audio src={src} controls className="w-full" data-testid="mist-result-audio" /> : <Loader2 size={18} className="text-gold animate-spin" />)}
      {asset?.id && (track === "music" || track === "video" || track === "image") && (
        <>
          <p className="mt-2 font-mono text-[9px] uppercase tracking-widest text-white/50" data-testid="mist-result-savehint">Not saved yet — choose where to keep it:</p>
          <ExportHandlers assetId={asset.id} track={track} filename={`mist_${track}`} />
        </>
      )}
      {result.description && <p className="font-mono text-[11px] text-white/80 whitespace-pre-wrap">{result.description}</p>}
    </div>
  );
}

/* Stage 5 export handlers — Save to Library (asset is already persisted in the
   DB tracking model on generation, so this confirms + refreshes the vault
   counters) and native Blob download to the device. Reused by Effects & Poem. */
const EXPORT_TRACK = {
  image: { t: "IMAGES", e: "png", al: "Mist Creations" },
  video: { t: "VIDEOS", e: "mp4", al: "Mist Reels" },
  music: { t: "AUDIO", e: "mp3", al: "Mist Audio" },
  audio: { t: "AUDIO", e: "mp3", al: "Mist Audio" },
  writing: { t: "IMAGES", e: "png", al: "Mist Creations" },
};

// Universal save panel: no auto-save — output persists only on an explicit tap.
// Device / Stock Library / Personal Library, for every AI generation tool.
// Universal save panel. Now a thin wrapper over the canonical <SaveDestinations/>
// (Download + Save… popup, founder-gated Stock, all-users Mist Library). Kept as
// a shim so the existing call sites (Mist Create, Poem, Effects ResultCard) get
// the new flow without touching each one. `assetId` here is a freshly-generated
// STAGED asset, so Mist Library save COMMITS it in place (commit=true).
const _EXPORT_TRACK_UP = { image: "IMAGES", video: "VIDEOS", music: "AUDIO", audio: "AUDIO", writing: "IMAGES" };
export function ExportHandlers({ assetId, filename, track = "image" }) {
  if (!assetId) return null;
  const up = _EXPORT_TRACK_UP[track] || "IMAGES";
  const ex = EXPORT_TRACK[track] || EXPORT_TRACK.image;
  return (
    <div className="mt-3" data-testid="export-handlers">
      <SaveDestinations assetId={assetId} track={up} filename={filename || `mist_${track}`} ext={ex.e} commit size="sm" />
    </div>
  );
}


/* Picks vault assets of a track (+ device upload → ingest into vault). */
export function AssetPicker({ track, selected = [], onChange, onItems, multi = false, max = 1, testid = "asset-picker" }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const fileRef = useRef(null);
  const bucket = { image: "images", video: "videos", music: "audio", writing: "poems" }[track];
  const label = { image: "image", video: "video", music: "track", writing: "poem" }[track] || track;

  const load = () => mistApi.library().then((d) => setItems(d[bucket] || [])).catch(() => setItems([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [track]);

  // Expose the resolved asset objects (with real asset ids) to callers that need
  // more than the filename (e.g. Ad Maker's logo/character asset_id payload).
  useEffect(() => {
    if (!onItems) return;
    onItems((items || []).filter((it) => selected.includes(it.filename)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, items]);

  const toggle = (fn) => {
    if (!multi) { onChange([fn]); setSheetOpen(false); return; }
    const has = selected.includes(fn);
    if (has) onChange(selected.filter((x) => x !== fn));
    else if (selected.length < max) onChange([...selected, fn]);
    else toast.error(`Max ${max} selected`);
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        if (multi && selected.length >= max) { toast.error(`Max ${max}`); break; }
        const r = await mistApi.ingest(f, track);
        onChange(multi ? [...selected, r.filename].slice(0, max) : [r.filename]);
      }
      await load();
      toast.success("Added to vault");
    } catch (err) { toast.error("Upload failed"); } finally { setBusy(false); }
  };

  return (
    <div data-testid={testid}>
      {/* Decoupled S20 action triggers — Vault picker vs. direct Device upload */}
      <div className="grid grid-cols-2 gap-3 w-full">
        <button type="button" onClick={() => setSheetOpen(true)} data-testid={`${testid}-open-vault`}
          className="flex flex-col items-center justify-center gap-1 bg-charcoal text-gold border-2 border-gold rounded-xl py-3 px-3 shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none active:scale-95 transition-transform duration-100">
          <FolderOpen size={18} strokeWidth={2.4} />
          <span className="font-mono text-[9px] font-black uppercase tracking-widest">Select From Vault</span>
        </button>
        <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} data-testid={`${testid}-upload`}
          className="flex flex-col items-center justify-center gap-1 bg-charcoal text-cream border-2 border-cream rounded-xl py-3 px-3 shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none active:scale-95 transition-transform duration-100 disabled:opacity-50">
          {busy ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} strokeWidth={2.4} />}
          <span className="font-mono text-[9px] font-black uppercase tracking-widest">Upload From Device</span>
        </button>
        <input ref={fileRef} type="file" hidden multiple={multi}
          accept={track === "image" ? "image/*" : track === "video" ? "video/*" : "audio/*"} onChange={onUpload} />
      </div>

      {/* Current selection preview strip */}
      {selected.length > 0 && (
        <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid={`${testid}-selected`}>
          {(items || []).filter((it) => selected.includes(it.filename)).map((it) => (
            <div key={it.id} className="relative w-11 h-11 rounded-md border-2 border-gold overflow-hidden shrink-0">
              <PickThumb item={it} />
            </div>
          ))}
          <span className="font-mono text-[8px] uppercase tracking-widest text-white/45">{selected.length}{multi ? `/${max}` : ""} selected</span>
        </div>
      )}

      {/* S20 bottom-sheet Vault tray — anchored to the bottom of the frame */}
      {sheetOpen && (
        <div className="absolute inset-0 z-[70] bg-black/70 flex items-end" data-testid={`${testid}-sheet`} onClick={() => setSheetOpen(false)}>
          <div className="w-full bg-charcoal border-t-2 border-gold rounded-t-2xl p-4 pb-8 shadow-[0_-4px_0px_#1A1A1A]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <FolderOpen size={15} className="text-gold" />
              <h3 className="flex-1 font-mono text-[11px] font-black uppercase tracking-widest text-cream truncate">Select {label}{multi ? ` · Max ${max}` : ""}</h3>
              <button onClick={() => setSheetOpen(false)} data-testid={`${testid}-sheet-close`} className="w-8 h-8 grid place-items-center border-2 border-cream/40 bg-black text-cream rounded-full active:scale-95"><X size={15} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3 overflow-y-auto max-h-[55vh] w-full pr-1 content-start no-scrollbar">
              {items == null ? (
                <div className="col-span-2 grid place-items-center py-8"><Loader2 size={18} className="text-gold animate-spin" /></div>
              ) : items.length === 0 ? (
                <p className="col-span-2 font-mono text-[10px] text-white/45 border-2 border-dashed border-white/15 rounded-xl py-6 text-center">No {label}s in your vault — upload one from your device.</p>
              ) : items.map((it) => {
                const on = selected.includes(it.filename);
                return (
                  <button key={it.id} type="button" onClick={() => toggle(it.filename)} data-testid={`pick-${it.id}`}
                    className={`relative aspect-square w-full bg-black rounded-xl border-2 overflow-hidden active:scale-95 transition-transform duration-100 ${on ? "border-gold" : "border-[#2a2a2a]"}`}>
                    <PickThumb item={it} />
                    {on && (
                      <div className="absolute inset-0 bg-gold/25 border-2 border-gold rounded-xl flex items-center justify-center">
                        <span className="flex items-center gap-1 bg-gold text-black font-mono text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full shadow-[2px_2px_0px_#1A1A1A]"><Check size={10} strokeWidth={3} /> Picked</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            {multi && (
              <button onClick={() => setSheetOpen(false)} data-testid={`${testid}-sheet-done`}
                className="mt-4 w-full bg-gold text-black border-2 border-charcoal rounded-xl py-3 font-mono text-[10px] font-black uppercase tracking-widest shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
                Done · {selected.length}/{max}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PickThumb({ item }) {
  const ref = useRef(null);
  const [src, setSrc] = useState(null);
  const [seen, setSeen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  // Only fetch a tile's thumbnail once it scrolls near the viewport.
  useEffect(() => {
    const el = ref.current;
    if (!el || seen) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setSeen(true); io.disconnect(); }
    }, { rootMargin: "250px" });
    io.observe(el);
    return () => io.disconnect();
  }, [seen]);
  useEffect(() => {
    if (!seen) return;
    let url; let alive = true;
    const load = item.track === "image" ? thumbAsset : item.track === "video" ? posterAsset : null;
    if (load) load(item.id).then((u) => { if (alive) { url = u; setSrc(u); } }).catch(() => {});
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [seen, item.id, item.track]);
  if (item.track === "music") return <div ref={ref} className="w-full h-full bg-[#9EFFB8] grid place-items-center"><Coins size={14} className="text-white" /></div>;
  return (
    <div ref={ref} className="relative w-full h-full overflow-hidden bg-charcoal">
      {/* Blur-up placeholder — keeps layout stable and shimmers until the WebP fades in */}
      {!loaded && <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-white/5 to-white/10" />}
      {src && (
        <img
          src={src}
          alt=""
          loading="lazy"
          onLoad={() => setLoaded(true)}
          className={`w-full h-full object-cover transition-[opacity,filter] duration-500 ${loaded ? "opacity-100 blur-0" : "opacity-0 blur-md"}`}
        />
      )}
    </div>
  );
}
